import {
  type ColumnType,
  type Selectable,
  type Insertable,
  type Updateable,
} from 'kysely'
import { z } from 'zod'

import { modemHistoryId, type ModemHistoryId } from './ModemHistory.js'

/** Identifier type for public.ingestion_ */
export type IngestionId = string & { __brand: 'IngestionId' }

/** Represents the table public.ingestion_ */
export default interface IngestionTable {
  /** Database type: pg_catalog.uuid */
  id: ColumnType<IngestionId, IngestionId | undefined, IngestionId>

  /** Database type: pg_catalog.text */
  source: ColumnType<string, string, string>

  /** Database type: pg_catalog.text */
  legacyReadingId: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.text */
  deviceIdentifier: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.int4 */
  modemHistoryId: ColumnType<
    ModemHistoryId | null,
    ModemHistoryId | null,
    ModemHistoryId | null
  >

  /** Database type: pg_catalog.int4 */
  modemMomsn: ColumnType<number | null, number | null, number | null>

  /** Database type: pg_catalog.timestamptz */
  tsReceived: ColumnType<Date, Date | string, Date | string>

  /** Database type: pg_catalog.timestamptz */
  completedAt: ColumnType<
    Date | null,
    Date | string | null,
    Date | string | null
  >

  /** Database type: pg_catalog.text */
  status: ColumnType<string, string | undefined, string>

  /** Database type: pg_catalog.int4 */
  readingsInserted: ColumnType<number | null, number | null, number | null>

  /** Database type: pg_catalog.text */
  errorMessage: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.text */
  dataPacketKey: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.jsonb */
  metadata: ColumnType<unknown | null, unknown | null, unknown | null>

  /** Database type: pg_catalog.text */
  idempotencyKey: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.timestamptz */
  createdAt: ColumnType<Date, Date | string | undefined, Date | string>

  /** Database type: pg_catalog.uuid */
  createdBy: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.timestamptz */
  updatedAt: ColumnType<Date, Date | string | undefined, Date | string>

  /** Database type: pg_catalog.uuid */
  updatedBy: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.timestamptz */
  deletedAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  deletedBy: ColumnType<string | null, string | null, string | null>
}

export type Ingestion = Selectable<IngestionTable>

export type IngestionInitializer = Insertable<IngestionTable>

export type IngestionMutator = Updateable<IngestionTable>

export const ingestionId = z.string() as unknown as z.Schema<IngestionId>

export const ingestion = z.object({
  id: ingestionId,
  source: z.string(),
  legacyReadingId: z.string().nullable().optional(),
  deviceIdentifier: z.string().nullable().optional(),
  modemHistoryId: modemHistoryId.nullable().optional(),
  modemMomsn: z.number().nullable().optional(),
  tsReceived: z.coerce.date(),
  completedAt: z.coerce.date().nullable().optional(),
  status: z.string(),
  readingsInserted: z.number().nullable().optional(),
  errorMessage: z.string().nullable().optional(),
  dataPacketKey: z.string().nullable().optional(),
  metadata: z.unknown().nullable().optional(),
  idempotencyKey: z.string().nullable().optional(),
  createdAt: z.coerce.date(),
  createdBy: z.string().nullable().optional(),
  updatedAt: z.coerce.date(),
  updatedBy: z.string().nullable().optional(),
  deletedAt: z.coerce.date().nullable().optional(),
  deletedBy: z.string().nullable().optional(),
}) as unknown as z.Schema<Ingestion>

export const ingestionInitializer = z.object({
  id: ingestionId.optional(),
  source: z.string(),
  legacyReadingId: z.string().optional().nullable(),
  deviceIdentifier: z.string().optional().nullable(),
  modemHistoryId: modemHistoryId.optional().nullable(),
  modemMomsn: z.number().optional().nullable(),
  tsReceived: z.coerce.date(),
  completedAt: z.coerce.date().optional().nullable(),
  status: z.string().optional(),
  readingsInserted: z.number().optional().nullable(),
  errorMessage: z.string().optional().nullable(),
  dataPacketKey: z.string().optional().nullable(),
  metadata: z.unknown().optional().nullable(),
  idempotencyKey: z.string().optional().nullable(),
  createdAt: z.coerce.date().optional(),
  createdBy: z.string().optional().nullable(),
  updatedAt: z.coerce.date().optional(),
  updatedBy: z.string().optional().nullable(),
  deletedAt: z.coerce.date().optional().nullable(),
  deletedBy: z.string().optional().nullable(),
}) as unknown as z.Schema<IngestionInitializer>

export const ingestionMutator = z.object({
  id: ingestionId.optional(),
  source: z.string().optional(),
  legacyReadingId: z.string().optional().nullable(),
  deviceIdentifier: z.string().optional().nullable(),
  modemHistoryId: modemHistoryId.optional().nullable(),
  modemMomsn: z.number().optional().nullable(),
  tsReceived: z.coerce.date().optional(),
  completedAt: z.coerce.date().optional().nullable(),
  status: z.string().optional(),
  readingsInserted: z.number().optional().nullable(),
  errorMessage: z.string().optional().nullable(),
  dataPacketKey: z.string().optional().nullable(),
  metadata: z.unknown().optional().nullable(),
  idempotencyKey: z.string().optional().nullable(),
  createdAt: z.coerce.date().optional(),
  createdBy: z.string().optional().nullable(),
  updatedAt: z.coerce.date().optional(),
  updatedBy: z.string().optional().nullable(),
  deletedAt: z.coerce.date().optional().nullable(),
  deletedBy: z.string().optional().nullable(),
}) as unknown as z.Schema<IngestionMutator>
